/**
 * Catálogo base de ejercicios (Req. 9.1, 9.2, 9.7; design.md, 11.1).
 *
 * - Los ids `ejb_<slug>` son FIJOS: rutinas y entrenamientos los guardan, así
 *   que nunca se cambian ni se reutilizan.
 * - asegurarCatalogoBase() agrega los que falten al arrancar y después de
 *   restaurar, y nunca modifica los que ya están (el usuario pudo cambiarles
 *   la unidad, las notas o archivarlos).
 * - Los ejercicios de las rutinas de Janeth (gimnasio y casa) llevan el
 *   nombre exacto de su documento (docs/Entrenamiento). No se agregan
 *   sinónimos sacados de nombres dudosos: solo los de uso común.
 * - Los de casa (0.3.5) son de peso corporal y van aparte de los del
 *   gimnasio aunque se parezcan: cada uno lleva su propia progresión.
 * - Los sinónimos no se guardan en la base: solo ayudan al buscador.
 */

import type { Equipo, GrupoMuscular, TipoEjercicio } from '@/db/types';

export interface EjercicioBase {
  id: string;
  nombre: string;
  grupoMuscular: GrupoMuscular;
  equipo: Equipo;
  tipo: TipoEjercicio;
  sinonimos: readonly string[];
  notas: string | null;
}

function e(
  slug: string,
  nombre: string,
  grupoMuscular: GrupoMuscular,
  equipo: Equipo,
  tipo: TipoEjercicio = 'peso_reps',
  sinonimos: readonly string[] = [],
  notas: string | null = null,
): EjercicioBase {
  return { id: `ejb_${slug}`, nombre, grupoMuscular, equipo, tipo, sinonimos, notas };
}

export const EJERCICIOS_BASE: readonly EjercicioBase[] = [
  // --- Pecho ------------------------------------------------------------------
  e('press_banca_barra', 'Press de banca con barra', 'pecho', 'barra', 'peso_reps', ['press banca', 'press plano', 'bench press']),
  e('press_banca_mancuernas', 'Press de banca con mancuernas', 'pecho', 'mancuernas', 'peso_reps', ['press plano con mancuernas']),
  e('press_inclinado_barra', 'Press inclinado con barra', 'pecho', 'barra', 'peso_reps', ['press banca inclinado']),
  e('press_inclinado_mancuernas', 'Press inclinado con mancuernas', 'pecho', 'mancuernas'),
  e('press_declinado_barra', 'Press declinado con barra', 'pecho', 'barra', 'peso_reps', ['press banca declinado']),
  e('press_banca_smith', 'Press de banca en Smith', 'pecho', 'smith'),
  e('press_pecho_maquina', 'Press de pecho en máquina', 'pecho', 'maquina', 'peso_reps', ['chest press']),
  e('aperturas_mancuernas', 'Aperturas con mancuernas', 'pecho', 'mancuernas', 'peso_reps', ['flys']),
  e('cruce_poleas', 'Cruce de poleas', 'pecho', 'polea', 'peso_reps', ['aperturas en polea', 'crossover']),
  e('aperturas_maquina', 'Aperturas en máquina', 'pecho', 'maquina', 'peso_reps', ['pec deck', 'mariposa']),
  e('lagartijas', 'Lagartijas', 'pecho', 'peso_corporal', 'reps', ['flexiones', 'push ups']),
  e('flexiones_rodillas', 'Flexiones con rodillas apoyadas', 'pecho', 'peso_corporal', 'reps', ['lagartijas de rodillas']),
  e('fondos_paralelas', 'Fondos en paralelas', 'pecho', 'peso_corporal', 'reps', ['dips']),
  e('pullover_mancuerna', 'Pullover con mancuerna', 'pecho', 'mancuernas'),

  // --- Espalda ----------------------------------------------------------------
  e('dominadas', 'Dominadas', 'espalda', 'peso_corporal', 'reps', ['pull ups']),
  e('dominadas_supinas', 'Dominadas con agarre supino', 'espalda', 'peso_corporal', 'reps', ['chin ups']),
  e('dominadas_negativas', 'Dominadas negativas', 'espalda', 'peso_corporal', 'reps'),
  e(
    'dominadas_asistidas',
    'Dominadas asistidas en máquina',
    'espalda',
    'maquina',
    'peso_reps',
    [],
    'La carga es la asistencia: menos carga es más difícil.',
  ),
  e('jalon_pecho', 'Jalón al pecho', 'espalda', 'polea', 'peso_reps', ['polea al pecho', 'lat pulldown', 'jalón dorsal']),
  e('jalon_cerrado', 'Jalón al pecho con agarre cerrado', 'espalda', 'polea', 'peso_reps', ['jalón con triángulo']),
  e('remo_barra', 'Remo con barra', 'espalda', 'barra', 'peso_reps', ['remo inclinado con barra']),
  e('remo_mancuerna', 'Remo con mancuerna a una mano', 'espalda', 'mancuernas', 'peso_reps', ['remo con mancuerna']),
  e('remo_polea_sentado', 'Remo sentado en polea', 'espalda', 'polea', 'peso_reps', ['remo en polea baja']),
  e('remo_maquina', 'Remo en máquina', 'espalda', 'maquina'),
  e('remo_t', 'Remo en T', 'espalda', 'barra', 'peso_reps', ['remo en barra T']),
  e('remo_invertido', 'Remo invertido', 'espalda', 'peso_corporal', 'reps'),
  e('pullover_polea', 'Pullover en polea', 'espalda', 'polea', 'peso_reps', ['jalón con brazos rectos']),
  e('peso_muerto_barra', 'Peso muerto con barra', 'espalda', 'barra', 'peso_reps', ['peso muerto convencional', 'deadlift']),
  e('hiperextensiones', 'Hiperextensiones', 'espalda', 'peso_corporal', 'reps', ['extensiones lumbares', 'banco romano']),
  e(
    'elevaciones_w_boca_abajo',
    'Elevaciones en W boca abajo',
    'espalda',
    'peso_corporal',
    'reps',
    [],
    'Trabajo de espalda superior; no equivale a un remo con carga.',
  ),
  e('encogimientos_mancuernas', 'Encogimientos con mancuernas', 'espalda', 'mancuernas', 'peso_reps', ['trapecio', 'shrugs']),

  // --- Hombros ----------------------------------------------------------------
  e('press_militar_barra', 'Press militar con barra', 'hombros', 'barra', 'peso_reps', ['press de hombro con barra']),
  e('press_hombro_mancuernas', 'Press de hombro con mancuernas', 'hombros', 'mancuernas', 'peso_reps', ['press militar con mancuernas']),
  e('press_hombro_maquina', 'Press de hombro en máquina', 'hombros', 'maquina'),
  e('press_arnold', 'Press Arnold', 'hombros', 'mancuernas'),
  e('elevacion_lateral_mancuernas', 'Elevación lateral con mancuernas', 'hombros', 'mancuernas', 'peso_reps', ['elevaciones laterales', 'vuelos laterales']),
  e('elevacion_lateral_polea', 'Elevación lateral en polea', 'hombros', 'polea', 'peso_reps', ['elevaciones laterales con polea']),
  e('elevacion_frontal', 'Elevación frontal con mancuernas', 'hombros', 'mancuernas', 'peso_reps', ['elevaciones frontales']),
  e('pajaros_mancuernas', 'Pájaros con mancuernas', 'hombros', 'mancuernas', 'peso_reps', ['deltoide posterior', 'elevaciones posteriores']),
  e('aperturas_inversas_maquina', 'Aperturas inversas en máquina', 'hombros', 'maquina', 'peso_reps', ['reverse pec deck']),
  e('face_pull', 'Face pull en polea', 'hombros', 'polea', 'peso_reps', ['face pull', 'jalón a la cara']),
  e('remo_menton', 'Remo al mentón', 'hombros', 'barra', 'peso_reps', ['upright row']),

  // --- Bíceps -----------------------------------------------------------------
  e('curl_barra', 'Curl con barra', 'biceps', 'barra', 'peso_reps', ['curl de bíceps con barra']),
  e('curl_barra_z', 'Curl con barra Z', 'biceps', 'barra', 'peso_reps', ['curl de bíceps con barra Z']),
  e('curl_mancuernas', 'Curl con mancuernas', 'biceps', 'mancuernas', 'peso_reps', ['curl alterno']),
  e('curl_martillo', 'Curl martillo', 'biceps', 'mancuernas'),
  e('curl_predicador_z', 'Curl predicador con barra Z', 'biceps', 'barra', 'peso_reps', ['curl scott']),
  e('curl_banco_inclinado', 'Curl en banco inclinado con mancuernas', 'biceps', 'mancuernas', 'peso_reps', ['curl inclinado']),
  e('curl_polea', 'Curl en polea', 'biceps', 'polea'),
  e('curl_concentrado', 'Curl concentrado', 'biceps', 'mancuernas'),

  // --- Tríceps ----------------------------------------------------------------
  e('extension_triceps_polea', 'Extensión de tríceps en polea', 'triceps', 'polea', 'peso_reps', ['jalón de tríceps', 'pushdown']),
  e('extension_triceps_cuerda', 'Extensión de tríceps con cuerda', 'triceps', 'polea', 'peso_reps', ['tríceps con cuerda']),
  e('extension_triceps_cabeza_mancuerna', 'Extensión de tríceps sobre la cabeza con mancuerna', 'triceps', 'mancuernas'),
  e('extension_triceps_cabeza_polea', 'Extensión de tríceps sobre la cabeza en polea', 'triceps', 'polea'),
  e('press_frances', 'Press francés con barra Z', 'triceps', 'barra', 'peso_reps', ['rompecráneos', 'skull crusher']),
  e('press_banca_cerrado', 'Press de banca con agarre cerrado', 'triceps', 'barra', 'peso_reps', ['press cerrado']),
  e('fondos_banco', 'Fondos en banco', 'triceps', 'peso_corporal', 'reps', ['fondos de tríceps']),
  e('patada_triceps', 'Patada de tríceps con mancuerna', 'triceps', 'mancuernas', 'peso_reps', ['kickback']),

  // --- Antebrazos -------------------------------------------------------------
  e('curl_muneca', 'Curl de muñeca con barra', 'antebrazos', 'barra'),
  e('curl_inverso_z', 'Curl inverso con barra Z', 'antebrazos', 'barra', 'peso_reps', ['curl con agarre prono']),

  // --- Abdomen ----------------------------------------------------------------
  e(
    'crunch_banco_plano',
    'Abdominal corto (crunch) en banco plano',
    'abdomen',
    'peso_corporal',
    'reps',
    ['crunch', 'abdominal corto'],
  ),
  e('crunch_suelo', 'Crunch en el suelo', 'abdomen', 'peso_corporal', 'reps', ['abdominales']),
  e('crunch_tapete', 'Abdominal corto (crunch) en tapete', 'abdomen', 'peso_corporal', 'reps'),
  e('crunch_polea', 'Crunch en polea', 'abdomen', 'polea', 'peso_reps', ['abdominal en polea']),
  e('elevacion_piernas_acostado', 'Elevación de piernas acostado', 'abdomen', 'peso_corporal', 'reps', ['leg raise']),
  e('elevacion_rodillas_colgado', 'Elevación de rodillas colgado', 'abdomen', 'peso_corporal', 'reps'),
  e('plancha', 'Plancha', 'abdomen', 'peso_corporal', 'tiempo', ['plank']),
  e('plancha_lateral', 'Plancha lateral', 'abdomen', 'peso_corporal', 'tiempo'),
  e('giro_ruso', 'Giro ruso', 'abdomen', 'peso_corporal', 'reps', ['russian twist']),
  e('rueda_abdominal', 'Rueda abdominal', 'abdomen', 'otro', 'reps', ['ab wheel']),
  e('press_pallof', 'Press Pallof', 'abdomen', 'polea', 'peso_reps', ['pallof']),
  e('dead_bug', 'Dead bug', 'abdomen', 'peso_corporal', 'reps'),
  e('bicicleta_abdominal', 'Bicicleta abdominal', 'abdomen', 'peso_corporal', 'reps', ['crunch bicicleta']),

  // --- Glúteos ----------------------------------------------------------------
  e(
    'hip_thrust_barra',
    'Hip thrust con barra y banco',
    'gluteos',
    'barra',
    'peso_reps',
    ['empuje de cadera', 'hip thrust'],
    'La parte alta de la espalda va apoyada en un banco firme que no se deslice.',
  ),
  e('hip_thrust_smith', 'Hip thrust en Smith', 'gluteos', 'smith'),
  e('puente_gluteos', 'Puente de glúteos en el suelo', 'gluteos', 'peso_corporal', 'reps', ['glute bridge']),
  e('puente_gluteos_una_pierna', 'Puente de glúteos a una pierna', 'gluteos', 'peso_corporal', 'reps'),
  e('puente_gluteos_pies_alejados', 'Puente de glúteos con pies algo más alejados', 'gluteos', 'peso_corporal', 'reps'),
  e('abduccion_cadera_lado', 'Abducción de cadera acostada de lado', 'gluteos', 'peso_corporal', 'reps'),
  e('maquina_abductora', 'Máquina abductora', 'gluteos', 'maquina', 'peso_reps', ['abductores', 'abducción de cadera']),
  e('patada_gluteo_polea', 'Patada de glúteo en polea', 'gluteos', 'polea', 'peso_reps', ['patada trasera']),
  e('patada_gluteo_maquina', 'Patada de glúteo en máquina', 'gluteos', 'maquina'),
  e('sentadilla_sumo', 'Sentadilla sumo con mancuerna', 'gluteos', 'mancuernas'),

  // --- Cuádriceps -------------------------------------------------------------
  e('sentadilla_barra', 'Sentadilla con barra', 'cuadriceps', 'barra', 'peso_reps', ['back squat', 'sentadilla trasera']),
  e('sentadilla_frontal', 'Sentadilla frontal con barra', 'cuadriceps', 'barra', 'peso_reps', ['front squat']),
  e('sentadilla_goblet', 'Sentadilla goblet', 'cuadriceps', 'mancuernas', 'peso_reps', ['sentadilla copa']),
  e('sentadilla_smith', 'Sentadilla en Smith', 'cuadriceps', 'smith'),
  e('sentadilla_hack', 'Sentadilla hack en máquina', 'cuadriceps', 'maquina', 'peso_reps', ['hack squat']),
  e('prensa', 'Prensa de piernas', 'cuadriceps', 'maquina', 'peso_reps', ['prensa', 'leg press']),
  e(
    'extension_rodillas',
    'Extensión de rodillas en máquina',
    'cuadriceps',
    'maquina',
    'peso_reps',
    ['extensión de piernas', 'extensión de cuádriceps', 'leg extension'],
  ),
  e(
    'desplante_estatico',
    'Desplante estático con mancuernas (sentadilla dividida)',
    'cuadriceps',
    'mancuernas',
    'peso_reps',
    ['split squat', 'zancada estática'],
    'Un pie delante y otro detrás en todas las repeticiones, sin juntar los pies. Una serie incluye las dos piernas.',
  ),
  e('desplante_estatico_sin_carga', 'Desplante estático sin carga', 'cuadriceps', 'peso_corporal', 'reps'),
  e('sentadilla_pausa_abajo', 'Sentadilla con pausa abajo', 'cuadriceps', 'peso_corporal', 'reps'),
  e('sentadilla_bajada_lenta', 'Sentadilla con bajada lenta', 'cuadriceps', 'peso_corporal', 'reps'),
  e('desplante_caminando', 'Desplante caminando con mancuernas', 'cuadriceps', 'mancuernas', 'peso_reps', ['zancadas', 'lunges']),
  e('desplante_inverso', 'Desplante inverso con mancuernas', 'cuadriceps', 'mancuernas', 'peso_reps', ['zancada hacia atrás']),
  e('sentadilla_bulgara', 'Sentadilla búlgara con mancuernas', 'cuadriceps', 'mancuernas', 'peso_reps', ['búlgara']),
  e('subidas_banco', 'Subidas al banco con mancuernas', 'cuadriceps', 'mancuernas', 'peso_reps', ['step up']),

  // --- Femorales --------------------------------------------------------------
  e('peso_muerto_rumano_barra', 'Peso muerto rumano con barra', 'femorales', 'barra', 'peso_reps', ['rumano', 'RDL']),
  e('peso_muerto_rumano_mancuernas', 'Peso muerto rumano con mancuernas', 'femorales', 'mancuernas', 'peso_reps', ['rumano con mancuernas']),
  e('curl_femoral_acostado', 'Curl femoral acostado', 'femorales', 'maquina', 'peso_reps', ['curl de pierna acostado', 'femoral acostado']),
  e('curl_femoral_sentado', 'Curl femoral sentado', 'femorales', 'maquina', 'peso_reps', ['curl de pierna sentado', 'femoral sentado']),
  e('buenos_dias', 'Buenos días con barra', 'femorales', 'barra', 'peso_reps', ['good morning']),
  e('curl_nordico', 'Curl nórdico', 'femorales', 'peso_corporal', 'reps', ['nordic curl']),

  // --- Pantorrillas -----------------------------------------------------------
  e('pantorrilla_pie_maquina', 'Elevación de pantorrilla de pie en máquina', 'pantorrillas', 'maquina'),
  e('pantorrilla_pie_mancuernas', 'Elevación de pantorrilla de pie con mancuernas', 'pantorrillas', 'mancuernas'),
  e('pantorrilla_sentado_maquina', 'Elevación de pantorrilla sentado en máquina', 'pantorrillas', 'maquina', 'peso_reps', ['costurera', 'pantorrilla sentado']),
  e('pantorrilla_prensa', 'Pantorrilla en prensa', 'pantorrillas', 'maquina'),
  e('pantorrilla_pie_sin_carga', 'Elevación de pantorrilla de pie', 'pantorrillas', 'peso_corporal', 'reps'),

  // --- Aductores --------------------------------------------------------------
  e('maquina_aductora', 'Máquina aductora', 'aductores', 'maquina', 'peso_reps', ['aductores', 'aducción de cadera']),

  // --- Cuerpo completo --------------------------------------------------------
  e('swing_kettlebell', 'Swing con kettlebell', 'cuerpo_completo', 'kettlebell', 'peso_reps', ['pesa rusa']),
  e('burpees', 'Burpees', 'cuerpo_completo', 'peso_corporal', 'reps'),
  e('sentadilla_press_mancuernas', 'Sentadilla con press de mancuernas', 'cuerpo_completo', 'mancuernas', 'peso_reps', ['thruster']),

  // --- Cardio -----------------------------------------------------------------
  e('caminadora', 'Caminadora', 'cardio', 'maquina', 'distancia', ['cinta', 'correr']),
  e('bicicleta_fija', 'Bicicleta fija', 'cardio', 'maquina', 'distancia', ['bici estática', 'spinning']),
  e('eliptica', 'Elíptica', 'cardio', 'maquina', 'tiempo'),
  e('escaladora', 'Escaladora', 'cardio', 'maquina', 'tiempo', ['escaleras']),
  e('remo_ergometro', 'Remo ergómetro', 'cardio', 'maquina', 'distancia', ['remadora']),
  e('saltar_cuerda', 'Saltar la cuerda', 'cardio', 'otro', 'tiempo', ['cuerda']),
  e('caminata', 'Caminata', 'cardio', 'peso_corporal', 'distancia', ['caminar']),
];

const POR_ID = new Map(EJERCICIOS_BASE.map((x) => [x.id, x]));

export function ejercicioBase(id: string): EjercicioBase | undefined {
  return POR_ID.get(id);
}

export const ETIQUETAS_GRUPO: Readonly<Record<GrupoMuscular, string>> = {
  pecho: 'Pecho',
  espalda: 'Espalda',
  hombros: 'Hombros',
  biceps: 'Bíceps',
  triceps: 'Tríceps',
  antebrazos: 'Antebrazos',
  abdomen: 'Abdomen',
  gluteos: 'Glúteos',
  cuadriceps: 'Cuádriceps',
  femorales: 'Femorales',
  pantorrillas: 'Pantorrillas',
  aductores: 'Aductores',
  cuerpo_completo: 'Cuerpo completo',
  cardio: 'Cardio',
};

export const ETIQUETAS_EQUIPO: Readonly<Record<Equipo, string>> = {
  barra: 'Barra',
  mancuernas: 'Mancuernas',
  maquina: 'Máquina',
  polea: 'Polea',
  peso_corporal: 'Peso corporal',
  kettlebell: 'Kettlebell',
  banda: 'Banda',
  smith: 'Smith',
  otro: 'Otro',
};

export const ETIQUETAS_TIPO: Readonly<Record<TipoEjercicio, string>> = {
  peso_reps: 'Carga y repeticiones',
  reps: 'Solo repeticiones',
  tiempo: 'Tiempo',
  distancia: 'Distancia y tiempo',
};
